<template>
  <transition
    enter-active-class="transition ease-out duration-200"
    enter-from-class="transform opacity-0 scale-105"
    enter-to-class="transform opacity-100 scale-100"
  >
    <div v-show="isOpen" class="cn-image-gallery">
      <div class="cn-image-gallery__wrapper">
        <div ref="modal" class="cut-corner cn-image-gallery__content">
          <span class="cn-image-gallery__close">
            <button class="cn-image-gallery__close-button" @click="close">
              <UiIcon name="close" />
            </button>
          </span>
          <swiper-container ref="swiperEl" init="false" class="swiper">
            <swiper-slide v-for="(image, i) in images" :key="i">
              <CdnImg
                provider="imgproxy"
                :src="image.src || '/img/placeholder.png'"
                :alt="image.alt"
                format="webp"
                :width="1024"
                :height="1024"
                loading="lazy"
              />
            </swiper-slide>
          </swiper-container>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, nextTick, watch, onMounted } from 'vue';
import type { Swiper } from 'swiper';
import { useMagicKeys, onClickOutside } from '@vueuse/core';
import { CdnImg } from '@webplatform/cdn-image';
import type { ImageWithAltType } from '~/types';
import { useBodyScrollLock } from '@/composables';
import UiIcon from '@/components/ui/UiIcon';

interface Props {
  images: ImageWithAltType[];
}

defineProps<Props>();

type SwiperContainerEl = HTMLElement & {
  swiper: Swiper;
  initialize: () => void;
};

const isOpen = ref(false);
const swiperEl = ref<SwiperContainerEl | null>(null);
useBodyScrollLock(() => isOpen.value);

onMounted(() => {
  if (!swiperEl.value) return;
  Object.assign(swiperEl.value, {
    rewind: true,
    navigation: true,
  });
  swiperEl.value.initialize();
});

const open = (index: number) => {
  isOpen.value = true;
  nextTick(() => {
    swiperEl.value?.swiper.update();
    swiperEl.value?.swiper.slideTo(index, 0, false);
  });
};

const close = () => {
  isOpen.value = false;
};

const { escape } = useMagicKeys();

watch(escape, (v) => {
  if (!v) return;
  if (isOpen.value === false) return;
  close();
});

const modal = ref(null);
onClickOutside(modal, () => close());
defineExpose({
  open,
  close,
});
</script>

<style lang="scss">
.cn-image-gallery {
  --ab-canyon-image-gallery-color-navigation: var(--ab-canyon-color-dark);
  --ab-canyon-image-gallery-color-backdrop: var(--ab-canyon-color-dark);
  --ab-canyon-image-gallery-color-bg: var(--ab-canyon-color-light);

  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: color-mix(
    in srgb,
    var(--ab-canyon-image-gallery-color-backdrop),
    transparent 70%
  );
  &__wrapper {
    padding: var(--size-base);
    max-width: 100%;
    height: 100%;
    aspect-ratio: 1 / 1;
    @media (min-width: 656px) {
      padding: var(--size-3xl);
    }
  }
  &__content {
    position: relative;
    height: 100%;
    background-color: var(--ab-canyon-image-gallery-color-bg);
  }
  &__close {
    z-index: 10;
    top: var(--size-lg);
    right: var(--size-lg);
    position: absolute;
  }
  &__close-button {
    padding: var(--size-xs);
    color: var(--ab-canyon-image-gallery-color-navigation);
    .cn-icon {
      width: var(--size-xl);
      height: var(--size-xl);
    }
  }
  .swiper {
    --swiper-navigation-color: var(--ab-canyon-image-gallery-color-navigation);
    height: 100%;
    width: auto;

    img {
      object-fit: contain;
      width: 100%;
      height: 100%;
    }
  }
}
</style>
